import { useEffect, useState } from "react";

/**
 * The two surfaces share a codebase but not a layout. The spec is explicit
 * that the desktop is not a stretched phone and the phone is not a shrunk
 * desktop, so the switch is a real branch rather than a set of breakpoints
 * bending one layout into the other.
 */
const DESKTOP = "(min-width: 1024px)";

/**
 * A finger rather than a mouse.
 *
 * Deliberately not the width query above. Width decides *layout* — whether the
 * desktop's three panes fit — and that is a different question from whether the
 * device has a native picker worth deferring to. An iPad in landscape is wider
 * than 1024px and still wants the wheel; a desktop window dragged narrow is not
 * a phone and should keep its keyboard.
 */
const TOUCH = "(pointer: coarse)";

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP);
}

/**
 * True where the platform has a better time picker than anything Frankie would
 * build — iOS's three-reel wheel, Android's clock dial. See TimeField.
 */
export function useIsTouch(): boolean {
  return useMediaQuery(TOUCH);
}
